'use client'

import { useState } from 'react'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import { SPECIALIZATIONS } from '@/config/specializations'
import { MultiSelect } from '@/components/ui/multi-select'
import { RangeInput } from '@/components/ui/range-input'
import { FilterDrawer } from '@/components/ui/filter-drawer'
import { BUDGET_TYPES, LOCATION_TYPES, JOB_TYPES, JOB_TYPE_CONFIG } from '@/lib/validations'

export interface JobFilters {
  specialization: string
  specializations: string[]
  locationType: string
  locationQuery: string
  budgetType: string
  budgetMin: number | null
  budgetMax: number | null
  jobType: string
  search: string
}

interface JobBoardFiltersProps {
  filters: JobFilters
  onFiltersChange: (filters: JobFilters) => void
  resultCount?: number
}

const LOCATION_LABELS: Record<string, string> = {
  remote: 'Remote',
  onsite: 'On-site',
  hybrid: 'Hybrid',
}

const BUDGET_LABELS: Record<string, string> = {
  hourly: 'Hourly',
  fixed: 'Fixed Price',
  monthly: 'Monthly',
}

export function JobBoardFilters({ filters, onFiltersChange, resultCount }: JobBoardFiltersProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  const hasActiveFilters =
    filters.specializations.length > 0 ||
    filters.locationType ||
    filters.locationQuery ||
    filters.budgetType ||
    filters.budgetMin !== null ||
    filters.budgetMax !== null ||
    filters.jobType !== ''

  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      specialization: '',
      specializations: [],
      locationType: '',
      locationQuery: '',
      budgetType: '',
      budgetMin: null,
      budgetMax: null,
      jobType: '',
    })
  }

  const filterControls = (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
      {/* Specializations multi-select */}
      <div className="w-full sm:w-56">
        <MultiSelect
          options={SPECIALIZATIONS.map((s) => ({ value: s.value, label: s.label }))}
          selected={filters.specializations}
          onChange={(values) =>
            onFiltersChange({
              ...filters,
              specializations: values,
              specialization: values[0] || '',
            })
          }
          placeholder="All specializations"
        />
      </div>

      {/* Work type */}
      <select
        value={filters.locationType}
        onChange={(e) => onFiltersChange({ ...filters, locationType: e.target.value, locationQuery: e.target.value === 'remote' || e.target.value === '' ? '' : filters.locationQuery })}
        className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] pl-3 pr-8 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
        style={font}
        aria-label="Filter by work type"
      >
        <option value="">Any</option>
        <option value="remote">Remote</option>
        <option value="hybrid">Hybrid</option>
        <option value="onsite">On-site</option>
      </select>

      {/* Country / location filter (visible for on-site and hybrid) */}
      {(filters.locationType === 'onsite' || filters.locationType === 'hybrid') && (
        <input
          type="text"
          placeholder="Country or city..."
          value={filters.locationQuery}
          onChange={(e) => onFiltersChange({ ...filters, locationQuery: e.target.value })}
          className="w-full sm:w-44 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
          style={font}
          aria-label="Filter by country or city"
        />
      )}

      {/* Job type */}
      <select
        value={filters.jobType}
        onChange={(e) => onFiltersChange({ ...filters, jobType: e.target.value })}
        className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] pl-3 pr-8 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
        style={font}
        aria-label="Filter by job type"
      >
        <option value="">All job types</option>
        {JOB_TYPES.map((type) => (
          <option key={type} value={type}>{JOB_TYPE_CONFIG[type].label}</option>
        ))}
      </select>

      {/* Budget type */}
      <select
        value={filters.budgetType}
        onChange={(e) => onFiltersChange({ ...filters, budgetType: e.target.value })}
        className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] pl-3 pr-8 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
        style={font}
        aria-label="Filter by budget type"
      >
        <option value="">All budget types</option>
        {BUDGET_TYPES.map((type) => (
          <option key={type} value={type}>{BUDGET_LABELS[type]}</option>
        ))}
      </select>

      {/* Budget range */}
      <div className="w-full sm:w-56">
        <RangeInput
          minValue={filters.budgetMin}
          maxValue={filters.budgetMax}
          onMinChange={(v) => onFiltersChange({ ...filters, budgetMin: v })}
          onMaxChange={(v) => onFiltersChange({ ...filters, budgetMax: v })}
          minPlaceholder="Min"
          maxPlaceholder="Max"
        />
      </div>

      {hasActiveFilters && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
          style={font}
        >
          <X className="h-3 w-3" />
          Clear filters
        </button>
      )}
    </div>
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input
            type="text"
            placeholder="Search jobs..."
            value={filters.search}
            onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
            className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] py-2 pl-9 pr-3 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
            style={font}
            aria-label="Search jobs"
          />
        </div>

        {/* Mobile filter toggle */}
        <button
          type="button"
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] sm:hidden"
          aria-label="Toggle filters"
          aria-expanded={showMobileFilters}
        >
          <SlidersHorizontal className="h-4 w-4 text-[var(--color-text-muted)]" />
        </button>
      </div>

      {/* Desktop filters (always visible) */}
      <div className="hidden sm:block">{filterControls}</div>

      {/* Mobile filters (drawer) */}
      <FilterDrawer isOpen={showMobileFilters} onClose={() => setShowMobileFilters(false)}>
        {filterControls}
      </FilterDrawer>

      {/* Result count */}
      {resultCount !== undefined && (
        <p className="text-xs text-[var(--color-text-muted)]" style={font}>
          {resultCount} {resultCount === 1 ? 'job' : 'jobs'} found
        </p>
      )}
    </div>
  )
}
